import { getBalanceEquity } from 'components/holdings_balance/selectorHelper';
import {
  convertExponentialToDecimal,
  convertFromUSDtoINR,
  cropAfterDecimals,
  getUSDT,
  numberCommaSeparator,
} from 'helpers/utils';
import { precisionForZeroBalance } from 'helpers/wallet';
import subscribe from 'HOC/subscribe';
import { inrConversionRateSelector } from 'selectors';
import { allOpenPositionsSelector } from 'selectors/holdingsSelectors';
import {
  openPositionMarkPriceSelectorById,
  walletSpotPriceBySymbol,
} from 'selectors/priceSelectors';
import { portfolioMarginSelector } from 'selectors/walletSelector';
import { useAppSelector } from 'storeHooks';
import { IBalance } from 'types/IWallet';
import Box from 'UIKit/Box';

interface WalletEquityProps {
  coinBalance: IBalance;
  returnUSDT?: boolean;
  className?: string;
  showFullPrecision?: boolean;
  returnInInr?: boolean;
}

const WalletEquity = subscribe(
  ({
    coinBalance,
    className,
    returnUSDT = false,
    showFullPrecision = false,
    returnInInr = false,
  }: WalletEquityProps) => {
    const openPositions = useAppSelector(allOpenPositionsSelector);

    const portfolioMarginData = portfolioMarginSelector();

    const inrConversionRate = useAppSelector(inrConversionRateSelector);

    const { positions_upl: positionsUpnl, asset_id: portfolioEnabledAssetId } =
      portfolioMarginData?.aggregate ?? {};

    const precision = showFullPrecision
      ? coinBalance?.asset?.precision
      : coinBalance?.asset?.minimum_precision ?? 2;

    const equity = convertExponentialToDecimal(
      getBalanceEquity(
        coinBalance,
        openPositions,
        openPositionMarkPriceSelectorById,
        positionsUpnl,
        portfolioEnabledAssetId
      )
    );

    const getValue = () => {
      if (returnUSDT) {
        return getUSDT(coinBalance?.asset?.symbol, equity, walletSpotPriceBySymbol);
      }

      if (returnInInr) {
        return cropAfterDecimals(
          convertFromUSDtoINR(equity, inrConversionRate),
          precision
        );
      }

      return cropAfterDecimals(equity, precision);
    };
    const commaSeperatedValue = numberCommaSeparator(getValue());
    const displayValue = precisionForZeroBalance(equity, commaSeperatedValue, precision);

    return (
      <Box as="span" className={className}>
        {displayValue}
      </Box>
    );
  }
);

WalletEquity.displayName = 'WalletEquity';

export { WalletEquity };
